import { useEffect, useRef } from 'react';

interface MainButtonOptions {
  text: string;
  onClick: () => void;
  isEnabled?: boolean;
  showProgress?: boolean;
  color?: string;
  textColor?: string;
}

export function useTelegramMainButton(options: MainButtonOptions | null) {
  const callbackRef = useRef(options?.onClick);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = options?.onClick;
  }, [options?.onClick]);

  // Effect 1: manage click listener lifecycle (mount/unmount only)
  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp) return;

    const handleClick = () => {
      callbackRef.current?.();
    };

    webApp.MainButton.onClick(handleClick);

    return () => {
      webApp.MainButton.offClick(handleClick);
      webApp.MainButton.hide();
      webApp.MainButton.hideProgress();
    };
  }, []); // mount/unmount only

  // Effect 2: update button params without hide/show flicker
  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp) return;

    if (!options) {
      webApp.MainButton.hide();
      return;
    }

    const { text, isEnabled = true, showProgress = false, color, textColor } = options;

    webApp.MainButton.setParams({
      text,
      is_active: isEnabled,
      is_visible: true,
      ...(color && { color }),
      ...(textColor && { text_color: textColor }),
    });

    if (showProgress) {
      webApp.MainButton.showProgress(true);
    } else {
      webApp.MainButton.hideProgress();
    }

    if (isEnabled) {
      webApp.MainButton.enable();
    } else {
      webApp.MainButton.disable();
    }

    webApp.MainButton.show();
  }, [options?.text, options?.isEnabled, options?.showProgress, options?.color, options?.textColor]);
}
